import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeftRight, CarFront, Hotel, Info, Plane, Ship, Sparkles, TrainFront } from "lucide-react";
import { api } from "@/api";
import { pick, useLang } from "@/i18n";
import { TransferCard } from "@/components/TransferCard";
import { TransferQuoteForm } from "@/components/TransferQuoteForm";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";

const CATEGORY_ICONS = {
    "transfer-fco": Plane,
    "transfer-cia": Plane,
    "transfer-hotel": Hotel,
    "transfer-termini": TrainFront,
    "transfer-civitavecchia": Ship,
    "transfer-hotel-hotel": ArrowLeftRight,
    "transfer-custom": Sparkles,
};

export default function Transfers() {
    const { lang, t } = useLang();
    const [params, setParams] = useSearchParams();
    const active = params.get("cat") || "";
    const [categories, setCategories] = useState([]);
    const [groups, setGroups] = useState(null);
    const [loadError, setLoadError] = useState(false);

    useEffect(() => {
        api.get("/public/categories").then((r) => setCategories(r.data.transfers || [])).catch(() => {});
    }, []);

    /* Unica fonte: gestionale Tourvoyagers (GET /api/website/transfers via backend).
       Nessun dato locale o inventato; errore → messaggio di indisponibilità temporanea. */
    useEffect(() => {
        setGroups(null);
        setLoadError(false);
        api.get("/public/transfers", { params: active ? { category: active } : {} })
            .then((r) => setGroups(r.data.groups || [r.data]))
            .catch(() => { setGroups([]); setLoadError(true); });
    }, [active]);

    const totalItems = (groups || []).reduce((a, g) => a + (g.items?.length || 0), 0);

    return (
        <div data-testid="transfers-page" className="marble-bg min-h-screen pt-32 pb-24">
            <Seo lang={lang} title={`${t.transfers.title} — TOURVOYAGERS`} description={t.transfers.sub} />
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <Reveal>
                    <p className="text-xs uppercase tracking-[0.3em] text-amber-400/90 font-medium">TOURVOYAGERS</p>
                    <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl text-amber-50 leading-tight">{t.transfers.title}</h1>
                    <p className="mt-5 max-w-2xl text-base text-stone-300 leading-relaxed">{t.transfers.sub}</p>
                </Reveal>

                <Reveal delay={0.1}>
                    <div className="mt-10 flex items-start gap-4 rounded-xl border border-amber-500/25 bg-[#121216] p-6" data-testid="optional-stop-note">
                        <Info size={20} className="mt-0.5 shrink-0 text-amber-400" />
                        <div>
                            <h3 className="font-display text-lg text-amber-100">{t.transfers.stopNoteTitle}</h3>
                            <p className="mt-1 text-sm text-stone-400 leading-relaxed">{t.transfers.stopNote}</p>
                        </div>
                    </div>
                </Reveal>

                <div className="mt-14">
                    <p className="text-xs uppercase tracking-[0.3em] text-amber-400/90 font-medium">{t.transfers.chooseCategory}</p>
                    <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                        {categories.map((c) => {
                            const Icon = CATEGORY_ICONS[c.slug] || CarFront;
                            const isActive = active === c.slug;
                            return (
                                <button
                                    key={c.slug}
                                    data-testid={`transfer-category-${c.slug}`}
                                    onClick={() => setParams(isActive ? {} : { cat: c.slug })}
                                    className={`gold-card flex items-center gap-3 rounded-xl p-5 text-left transition-all ${isActive ? "!border-amber-400/70 bg-amber-400/10" : ""}`}
                                >
                                    <Icon size={20} className="shrink-0 text-amber-400" />
                                    <span className="text-sm font-medium text-stone-200">{c.label[lang] || c.label.it}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <Reveal delay={0.15}>
                    <TransferQuoteForm categories={categories} />
                </Reveal>

                <div className="mt-16" data-testid="transfer-packages">
                    {groups === null ? (
                        <p className="text-sm text-stone-500">{t.common.loading}</p>
                    ) : loadError ? (
                        <div className="rounded-xl border border-amber-500/25 bg-[#121216] p-8 text-center" data-testid="catalog-error">
                            <p className="text-sm text-stone-300">{t.common.catalogError}</p>
                        </div>
                    ) : totalItems === 0 ? (
                        <p className="text-sm text-stone-500" data-testid="transfer-empty">{t.transfers.empty}</p>
                    ) : (
                        groups.filter((g) => g.items?.length > 0).map((g) => (
                            <div key={g.slug || g.category} className="mb-14" data-testid={`transfer-group-${g.slug || g.category}`}>
                                {!active && (
                                    <h2 className="font-display text-2xl text-amber-100">{pick(g.label, lang)}</h2>
                                )}
                                <div className="mt-6 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                                    {g.items.map((item, i) => <TransferCard key={item.id || i} item={item} index={i} />)}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}
